import { Fetcher } from '@this/src/util';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { Modal, ModalBody, ModalHeader } from '@this/components/shared/ui/feedbacks/modal';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Loading } from '@this/components/shared/ui/feedbacks/loading';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import { Select } from '@this/components/shared/ui/inputs/select';
import { Textarea } from '@this/components/shared/ui/inputs/textarea';
import type {
  AnaBookingInventoryCommentArgs,
  CommentType
} from '@this/domain/arrangement/ana_booking_inventory_comment';
import {
  AnaBookingInventoryComment,
  commentTypeLabels
} from '@this/domain/arrangement/ana_booking_inventory_comment';
import Notification from '@this/src/notification';

interface Props {
  inventoryPairId: number;
  onClose: () => void;
}

const getStatusStyle = (status: CommentType | undefined, theme: AITTheme): React.CSSProperties => {
  switch (status) {
    case 'important':
      return { color: theme.tokens.colors.danger.primary, fontWeight: 'bold' };
    case 'warning':
      return { color: theme.tokens.colors.warning.primary, fontWeight: 'bold' };
    default:
      return {};
  }
};

const InventoryCommentModal = observer(({ inventoryPairId, onClose }: Props) => {
  const { theme } = useAITTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [comments, setComments] = useState<AnaBookingInventoryComment[]>([]);
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<CommentType>('normal');

  const fetchComments = () => {
    setIsLoading(true);
    Fetcher.get<{ comments: AnaBookingInventoryCommentArgs[] }>(
      `/arrangement/auto_reservation_inventory/ana_booking/inventory_pairs/${inventoryPairId}/comments.json`
    )
      .then(res => {
        const instances = res.comments.map(args => new AnaBookingInventoryComment(args));
        setComments(instances);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const saveComment = () => {
    const params = {
      content,
      status,
      inventory_pair_id: inventoryPairId
    };
    setIsLoading(true);
    Fetcher.post(
      `/arrangement/auto_reservation_inventory/ana_booking/inventory_pairs/${inventoryPairId}/comments.json`,
      params
    )
      .then(() => {
        setContent('');
        setStatus('normal');
        fetchComments();
      })
      .catch(() => {
        Notification.error('コメントの保存に失敗しました');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchComments();
  }, [inventoryPairId]);

  return (
    <Modal open onClose={onClose} size="large">
      <ModalHeader>この商品に関するやり取り</ModalHeader>
      <ModalBody>
        {isLoading ? (
          <Loading />
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell nowrap>手配者名</TableCell>
                  <TableCell nowrap>種類</TableCell>
                  <TableCell>内容</TableCell>
                  <TableCell nowrap>記載日時</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comments.map(c => (
                  <TableRow key={c.id}>
                    <TableCell nowrap>{c.commentedByName}</TableCell>
                    <TableCell nowrap style={getStatusStyle(c.status, theme)}>
                      {commentTypeLabels[c.status]}
                    </TableCell>
                    <TableCell>{c.content}</TableCell>
                    <TableCell nowrap>{moment(c.commentedAt).format('YYYY-MM-DD HH:mm')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div style={{ margin: '10px 0' }}>
          <Select value={status} onChange={e => setStatus(e.target.value as CommentType)}>
            {Object.entries(commentTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <div style={{ margin: '10px 0' }}>
          <Textarea
            placeholder="コメント内容を入力"
            value={content}
            onChange={e => setContent(e.target.value)}
            rowsMin={5}
            size="medium"
            fullWidth
          />
        </div>

        <Button onClick={saveComment}>送信</Button>
      </ModalBody>
    </Modal>
  );
});

export default InventoryCommentModal;
